import { IconButton } from '@atlaskit/button/new';
import { cssMap } from '@atlaskit/css';
import Heading from '@atlaskit/heading';
import CrossIcon from '@atlaskit/icon/core/cross';
import Image from '@atlaskit/image';
import { Box, Inline, Pressable, Stack, Text } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';
import { type AdControls } from '@post-office/components--ad-controls';
import { type NextBestAction as NextBestActionType } from '@post-office/confluence-next-best-action';
import { type ComponentProps } from 'react';
import { useIntl } from 'react-intl-next';

import HeartImage from './images/heart.svg';

export type Props = {
	onSideNavClick: (nextBestActionType: NextBestActionType) => string | void | null;
	whyAmISeeingThisModalContent?: ComponentProps<typeof AdControls>['whyAmISeeingThisModalContent'];
	handleOnDismissed: () => void;
	nextBestActionType: NextBestActionType;
};

const styles = cssMap({
	container: {
		width: 'auto',
		borderRadius: token('radius.small'),
		borderWidth: token('border.width'),
		borderColor: token('color.border'),
		borderStyle: 'solid',
		backgroundColor: token('color.background.accent.blue.subtlest'),
		margin: token('space.150'),
		paddingBlock: token('space.150'),
		paddingInlineEnd: token('space.150'),
		paddingInlineStart: token('space.075'),
	},
	link: {
		color: token('color.link.pressed'),
		fontWeight: token('font.weight.medium'),
		cursor: 'pointer',
		padding: '0',
		backgroundColor: 'transparent',

		'&:hover': {
			textDecoration: 'underline',
		},
		'&:active': {
			color: token('color.link.pressed'),
		},
	},
});

export const ResurrectedUserWelcomeMessage = ({
	onSideNavClick,
	handleOnDismissed,
	nextBestActionType,
}: Props) => {
	const intl = useIntl();
	const headerText = intl.formatMessage({
		id: 'post-office.confluence-resurrected-user-welcome-back.next-best-action.header',
		defaultMessage: 'Welcome back!',
		description: 'Title shown when displaying the welcome back nudge for resurrected users.',
	});

	const bodyText = intl.formatMessage({
		id: 'post-office.confluence-resurrected-user-welcome-back.body',
		defaultMessage: 'Jump into the top discussions and pages you might have missed.',
		description: 'Body for confluence side nav welcome back nudge for resurrected users.',
	});

	const ctaMessage = intl.formatMessage({
		id: 'post-office.confluence-resurrected-user-welcome-back.cta',
		defaultMessage: "See what's popular",
		description: 'CTA button text for the welcome back nudge for resurrected users.',
	});

	const closeLabel = intl.formatMessage({
		id: 'post-office.confluence-resurrected-user-welcome-back.close',
		defaultMessage: 'Close',
		description: 'Label for the close icon that is represented by an X',
	});

	const imageAltText = intl.formatMessage({
		id: 'post-office.confluence-resurrected-user-welcome-back.image-alt',
		defaultMessage: 'Two hearts overlapped with each other',
		description: 'Alternate text for picture of two hearts',
	});

	// TODO
	const handleDismissClicked = () => {
		void handleOnDismissed();
	};

	// TODO
	const handleCtaClicked = () => {
		void onSideNavClick(nextBestActionType);
	};

	return (
		<Box xcss={styles.container} testId="confluence-resurrected-user-welcome-back-message">
			<Inline shouldWrap={false} space="space.100">
				<Image src={HeartImage} alt={imageAltText} />
				<Stack alignInline="start" grow="fill" space="space.100">
					<Inline shouldWrap={false} alignBlock="center" grow="fill" spread="space-between">
						<Heading size="xsmall">{headerText}</Heading>
						<IconButton
							icon={CrossIcon}
							appearance="subtle"
							label={closeLabel}
							isTooltipDisabled={false}
							onClick={handleDismissClicked}
							spacing="compact"
						/>
					</Inline>
					<Text size="small">{bodyText}</Text>
					<Pressable xcss={styles.link} testId="cta-link" onClick={handleCtaClicked}>
						{ctaMessage}
					</Pressable>
				</Stack>
			</Inline>
		</Box>
	);
};
